import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

export function useProductProviders(page = 1, pageSize = 100, search = '', sortBy = 'name', sortOrder = 'asc') {
  const [providers, setProviders] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchProviders()
  }, [page, search, sortBy, sortOrder])

  const fetchProviders = async () => {
    try {
      setLoading(true)
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      let query = supabase
        .from('product_providers')
        .select('*', { count: 'exact' })

      if (search) {
        query = query.ilike('name', `%${search}%`)
      }

      const { data, count, error: err } = await query
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to)

      if (err) throw err
      setProviders(data || [])
      setTotal(count || 0)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createProvider = async (data) => {
    const { data: result, error } = await supabase
      .from('product_providers')
      .insert([data])
      .select()
    if (error) throw error
    await fetchProviders()
    return result[0]
  }

  const updateProvider = async (id, data) => {
    const { data: result, error } = await supabase
      .from('product_providers')
      .update(data)
      .eq('id', id)
      .select()
    if (error) throw error
    await fetchProviders()
    return result[0]
  }

  const toggleActive = async (id, currentValue) => {
    setProviders(prev => prev.map(p => p.id === id ? { ...p, active: !currentValue } : p))
    const { error } = await supabase
      .from('product_providers')
      .update({ active: !currentValue })
      .eq('id', id)
    if (error) {
      setProviders(prev => prev.map(p => p.id === id ? { ...p, active: currentValue } : p))
      console.error('toggleActive error:', error)
    }
  }

  // Returns { imported: number, skipped: string[] }
  const importProviders = async (rows) => {
    const names = rows.map(r => r.name).filter(Boolean)
    const { data: existing } = await supabase
      .from('product_providers')
      .select('name')
      .in('name', names)
    const existingNames = new Set((existing || []).map(r => r.name))

    const newRows = rows.filter(r => !existingNames.has(r.name))
    const skipped = rows.filter(r => existingNames.has(r.name)).map(r => r.name)

    if (newRows.length > 0) {
      const { error } = await supabase.from('product_providers').insert(newRows)
      if (error) throw error
    }

    await fetchProviders()
    return { imported: newRows.length, skipped }
  }

  return { providers, total, loading, error, createProvider, updateProvider, toggleActive, importProviders, refetch: fetchProviders }
}
